import React from "react";
import { ArrowRight, CircleAlert, FilePenLine } from "lucide-react";
import { Button } from "../../ui/button";
import { BotErrorIcon } from "./BotIcons";
import { useTranslation } from "../../../hooks/useTranslation";

const SUPPORT_URL = "https://pingup.ai/contact-us";

/**
 * Pagina di errore dell'analisi sito — frame Figma 4060:1206
 * ("Registrazione - error"): bot rosso, titolo "Ops!", box rosso con la
 * causa SPECIFICA del fallimento, e due uscite: riprovare con un sito o
 * passare alla configurazione manuale.
 */
const AnalysisErrorPage = ({ error, onRetry, onSwitchToCustom }) => {
  const { t } = useTranslation();

  return (
    <div className="pu-error-in -mx-4 -mb-4 flex min-h-[calc(100vh-4rem)] flex-col items-center bg-[#F9FAFB] px-4 pt-14">
      <style>{`
        @keyframes puErrorIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        .pu-error-in { animation: puErrorIn .5s ease-out both; }
        @media (prefers-reduced-motion: reduce) { .pu-error-in { animation: none; } }
      `}</style>

      <BotErrorIcon />

      <h1 className="mt-5 text-center text-[32px] font-semibold leading-9 text-[#020617]">
        {t("onboardingWizard.errorPageTitle")}
      </h1>
      <p className="mt-4 max-w-xl text-center text-base leading-7 text-[#64748B]">
        {t("onboardingWizard.errorPageSubtitle")}
      </p>

      {/* Box rosso con la causa specifica (titolo + spiegazione) */}
      <div className="mt-10 flex w-full max-w-[455px] items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-4 py-4">
        <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
        <div>
          <p className="text-sm font-semibold text-red-600">{error.title}</p>
          <p className="mt-1 text-sm leading-relaxed text-red-600">
            {error.description}
          </p>
        </div>
      </div>

      <p className="mt-12 text-base font-semibold text-[#020617]">
        {t("onboardingWizard.howToProceed")}
      </p>

      <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:items-center">
        <Button
          type="button"
          variant="secondary"
          className="bg-blue-100 text-[#0F172A] hover:bg-blue-200"
          onClick={onSwitchToCustom}
        >
          <FilePenLine className="mr-2 h-4 w-4" />
          {t("onboardingWizard.errorManualCta")}
        </Button>
        <Button type="button" onClick={onRetry}>
          {t("onboardingWizard.errorRetryCta")}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      <p className="mt-12 text-xs text-[#64748B]">
        {t("onboardingWizard.errorHelpText")}{" "}
        <a
          href={SUPPORT_URL}
          target="_blank"
          rel="noreferrer"
          className="font-semibold underline underline-offset-2 hover:text-foreground"
        >
          {t("onboardingWizard.errorHelpLink")}
        </a>
      </p>
    </div>
  );
};

export default AnalysisErrorPage;
